import React from "react";
import EkselensMark from "./EkselensMark";

/**
 * Ekselens Consulting logo — landscape composition.
 *
 * Uses the EXACT geometric brand mark (5 purple angular shards) sourced
 * directly from the live ekselens.com site, paired with the
 * "EKSELENS / CONSULTING" wordmark in Work Sans.
 *
 * The mark is sized slightly TALLER than the wordmark stack to match the
 * proportions of the original ekselens.com header (and to ensure all five
 * shards remain fully visible at small header sizes).
 *
 *  Variants:
 *   - "on-dark"  — purple glyph + white wordmark (header / footer)
 *   - "on-light" — purple glyph + black wordmark
 */

const SIZES = {
  sm: { mark: 32, h1: 12, h2: 7.5, gap: 10 },
  md: { mark: 48, h1: 18, h2: 10, gap: 14 },
  lg: { mark: 72, h1: 26, h2: 13, gap: 18 },
};

const Logo = ({ variant = "on-dark", size = "md", className = "", logoUrl = "", brandName = "", color = "#7737F6" }) => {
  const sizes = SIZES[size] || SIZES.md;
  const onDark = variant === "on-dark";
  const wordColor = onDark ? "#FFFFFF" : "#0B0B0C";
  const subColor = onDark ? "#FFFFFF" : "#3A3A40";

  // White-label: a per-instance custom logo replaces the Ekselens mark entirely.
  if (logoUrl) {
    return (
      <img src={logoUrl} alt={brandName || "Logo"} loading="eager"
        style={{ height: sizes.mark, width: "auto" }}
        className={`inline-block ${className}`} data-testid="ekselens-logo" />
    );
  }

  // Default mark + wordmark. For the Ekselens primary instance this is identical
  // to before; a white-label instance recolours the mark + shows its own name.
  const isDefaultBrand = !brandName || brandName.trim().toLowerCase() === "ekselens";
  const word = isDefaultBrand ? "EKSELENS" : brandName.toUpperCase();

  return (
    <div
      className={`inline-flex items-center ${className}`}
      style={{ gap: sizes.gap }}
      data-testid="ekselens-logo"
    >
      <EkselensMark size={sizes.mark} color={color} loading="eager" />

      <div className="flex flex-col leading-none">
        <span
          style={{
            fontFamily: "Work Sans, sans-serif",
            fontWeight: 500,
            letterSpacing: "0.055em",
            color: wordColor,
            fontSize: sizes.h1,
            lineHeight: 1,
          }}
        >
          {word}
        </span>
        {isDefaultBrand && (
        <span
          style={{
            fontFamily: "Work Sans, sans-serif",
            fontWeight: 400,
            letterSpacing: "0.32em",
            color: subColor,
            fontSize: sizes.h2,
            marginTop: 5,
            lineHeight: 1,
            opacity: onDark ? 0.85 : 0.7,
          }}
        >
          CONSULTING
        </span>
        )}
      </div>
    </div>
  );
};

export default Logo;
